import React, { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Position } from "react-avatar-editor";
import { Box, Button, Dialog, DialogContent, DialogTitle, IconButton, Stack, Typography, useTheme } from "@mui/material";
import { Close, DeleteForever, Edit } from "@mui/icons-material";
import { useSnackbar } from "notistack";

import SelectAvatarImage from "./SelectAvatarImage";
import EditAvatarImage, { GetImageHandler } from "./EditAvatarImage";
import PreviewAvatarImage from "./PreviewAvatarImage";
import ChangeAvatarImage from "./ChangeAvatarImage";

interface AvatarImageSelectorProps {
  /**
   * The current avatar image.
   */
  image?: string;
  /**
   * If `true`, the component is shown.
   * @default false
   */
  isOpen: boolean;
  /**
   * Callback fired when the the avatar image is changed.
   * @param image The image as a base64 encoded string.
   */
  onChange(image: string): void;
  /**
   * Callback fired when the component requests to be closed.
   */
  onClose: () => void;
  /**
   * Scale the image down to the editor size.
   * @default true
   */
  scaleImage?: boolean;
}

enum ImageSelectorState {
  ChangeImage,
  SelectImage,
  EditImage,
  PreviewImage
}

const defaultPosition: Position = {x: 0.5, y: 0.5};
const defaultZoom: number = 0;

export default function AvatarImageSelector(props: AvatarImageSelectorProps): React.JSX.Element {
  const { i18n } = useTranslation();
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();
  const editAvatarImageRef = useRef<GetImageHandler>(null);

  const startingState = props.image ? ImageSelectorState.ChangeImage : ImageSelectorState.SelectImage;

  const [selectorState, setSelectorState] = useState<ImageSelectorState>(startingState);

  // Avatar image details:
  // The image file the user selected
  const [file, setFile] = useState<File | null>(null);
  // The base64 encoded string representing the image
  const [encoded, setEncoded] = useState<string>("");
  // The point to center the image around
  const [position, setPosition] = useState<Position>(defaultPosition);
  // The zoom step for the image
  const [zoom, setZoom] = useState<number>(defaultZoom);

  function handleClose(): void {
    props.onClose();
  }

  function handleChangeImage(): void {
    setSelectorState(ImageSelectorState.SelectImage);
  }

  function handleRemoveImage(): void {
    enqueueSnackbar(i18n.t("avatar-image-selector.avatar-image-removed"), { variant: "success" });
    props.onChange("");
    props.onClose();
  }

  function handleImageSelected(file: File): void {
    setFile(file);
    setPosition(defaultPosition);
    setZoom(defaultZoom);
    setSelectorState(ImageSelectorState.EditImage);
  }

  function handlePositionChange(value: Position): void {
    setPosition(value);
  }

  function handleZoomChange(value: number): void {
    setZoom(value);
  }

  function handleImageEdited(): void {
    const encoded = editAvatarImageRef.current?.getImageAsBase64EncodedString();
    if (encoded) {
      setEncoded(encoded);
      setSelectorState(ImageSelectorState.PreviewImage);
    }
  }

  function handleImageSave(): void {
    enqueueSnackbar(i18n.t("avatar-image-selector.avatar-image-saved"), { variant: "success" });
    props.onChange(encoded);
  }

  function getTitle(): string {
    switch(selectorState) {
      case ImageSelectorState.ChangeImage:
        return i18n.t("avatar-image-selector.titles.change-image");
      case ImageSelectorState.EditImage:
        return i18n.t("avatar-image-selector.titles.edit-image");
      case ImageSelectorState.PreviewImage:
        return i18n.t("avatar-image-selector.titles.preview-image");
      case ImageSelectorState.SelectImage:
        return i18n.t("avatar-image-selector.titles.select-image");
      default:
        return "";
    }
  }

  function getButtons(): React.JSX.Element {
    switch(selectorState) {
      case ImageSelectorState.ChangeImage: {
        return (
          <Stack direction="row" spacing={1} sx={{
            justifyContent: "space-around"
          }}>
            <Button variant="contained" startIcon={<Edit />} onClick={handleChangeImage}>
              {i18n.t("avatar-image-selector.change-button")}
            </Button>
            <Button variant="contained" startIcon={<DeleteForever />} color="error" onClick={handleRemoveImage}>
              {i18n.t("avatar-image-selector.remove-button")}
            </Button>
          </Stack>
        );
      }
      case ImageSelectorState.SelectImage: {
        return (
          <Stack direction="row" spacing={1} sx={{
            justifyContent: "end"
          }}>
            <Button variant="contained" color="secondary" onClick={handleClose}>{i18n.t("avatar-image-selector.cancel-button")}</Button>
          </Stack>
        );
      }
      case ImageSelectorState.EditImage: {
        return (
          <Stack direction="row" spacing={1}>
            <Box sx={{
              flex: 1
            }}>
              <Button variant="contained" onClick={() => setSelectorState(ImageSelectorState.SelectImage)}>{i18n.t("avatar-image-selector.back-button")}</Button>
            </Box>
            <Button variant="contained" color="secondary" onClick={handleClose}>{i18n.t("avatar-image-selector.cancel-button")}</Button>
            <Button variant="contained" color="success" onClick={handleImageEdited}>{i18n.t("avatar-image-selector.next-button")}</Button>
          </Stack>
        );
      }
      case ImageSelectorState.PreviewImage: {
        return (
          <Stack direction="row" spacing={1}>
            <Box sx={{
              flex: 1
            }}>
              <Button variant="contained" onClick={() => setSelectorState(ImageSelectorState.EditImage)}>{i18n.t("avatar-image-selector.back-button")}</Button>
            </Box>
            <Button variant="contained" color="secondary" onClick={handleClose}>{i18n.t("avatar-image-selector.cancel-button")}</Button>
            <Button variant="contained" color="success" onClick={handleImageSave}>{i18n.t("avatar-image-selector.save-button")}</Button>
          </Stack>
        );
      }
    }
  }

  return (
    <Dialog open={props.isOpen}>
      <DialogTitle>
        <Typography variant="h3">{getTitle()}</Typography>
      </DialogTitle>
      <IconButton onClick={handleClose} sx={{position: "absolute", right: 8, top: 8}}>
        <Close />
      </IconButton>
      <DialogContent dividers sx={{minWidth: 400}}>
        <Stack spacing={1}>
          <Box>
            {selectorState === ImageSelectorState.ChangeImage && props.image &&
              <ChangeAvatarImage image={props.image} />
            }
            {selectorState === ImageSelectorState.SelectImage &&
              <SelectAvatarImage onSelect={handleImageSelected}/>
            }
            {selectorState === ImageSelectorState.EditImage && file &&
              <EditAvatarImage
                ref={editAvatarImageRef}
                file={file}
                onPositionChange={handlePositionChange}
                onZoomChange={handleZoomChange}
                position={position}
                scaleImage={props.scaleImage ?? true}
                zoom={zoom} />
            }
            {selectorState === ImageSelectorState.PreviewImage &&
              <PreviewAvatarImage image={encoded} />
            }
          </Box>
          <Box>
            {getButtons()}
          </Box>
        </Stack>
      </DialogContent>
    </Dialog>
  );
}
